import {
  deleteEvaluation,
  downloadEvaluationDocument,
  getEvaluation,
  pauseEvaluation,
  resumeEvaluation,
} from "../Api/evaluations";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { evaluationModelAccessResponse } from "../Models/evaluationModel";
import { fetchEvaluate, fetchModelOptions } from "../Api/llm";
import { downloadEvaluation } from "../Api/evaluations";
import { AppendixItem, SingleEvaluationResponse } from "../Models/evaluation";
import { AxiosResponse } from "axios";

export const useEvaluationModel = () => {
  return useQuery<evaluationModelAccessResponse, Error>({
    queryKey: ["evaluationModel"],
    queryFn: () => fetchModelOptions(),
  });
};

export const usePauseEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluationId: string) => pauseEvaluation(evaluationId),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["evaluations"],
      });

      queryClient.invalidateQueries({
        queryKey: ["evaluationStatus"],
      });

      queryClient.invalidateQueries({
        queryKey: ["checkProgress"],
      });
    },
  });
};

export const useResumeEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluationId: string) => resumeEvaluation(evaluationId),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["evaluations"],
      });

      queryClient.invalidateQueries({
        queryKey: ["evaluationStatus"],
      });

      queryClient.invalidateQueries({
        queryKey: ["checkProgress"],
      });
    },
  });
};

export const useFetchEvaluation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      selectedCompanyId: number;
      selectedModelId: number;
      questionListId: string;
      certificationId: string;
      languageId: number;
      appendix: AppendixItem[];
      configOverrides?: Record<string, number | boolean | string>;
    }) =>
      fetchEvaluate(
        variables.selectedCompanyId,
        variables.selectedModelId,
        variables.questionListId,
        variables.certificationId,
        variables.languageId,
        variables.appendix,
        variables.configOverrides,
      ),
    onSuccess: (_, { selectedCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: ["evaluations"] });
      queryClient.invalidateQueries({
        queryKey: ["singleCompany", selectedCompanyId.toString()],
      });
    },
  });
};

export const useDownloadEvaluation = () => {
  return useMutation<AxiosResponse, Error, string>({
    mutationFn: (user_evaluation_request_id: string) =>
      downloadEvaluation(user_evaluation_request_id),
  });
};

export const useDeleteEvaluation = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (evaluationId: string) => deleteEvaluation(evaluationId),
    onSuccess: (_, evaluationId) => {
      queryClient.invalidateQueries({ queryKey: ["evaluations"] });
      queryClient.invalidateQueries({
        queryKey: ["singleEvaluation", evaluationId],
      });
      queryClient.invalidateQueries({ queryKey: ["singleCompany", companyId] });
    },
  });
};

export const useDownloadEvaluationDocument = () => {
  return useMutation<AxiosResponse, Error, { evaluationId: number }>({
    mutationFn: ({ evaluationId }: { evaluationId: number }) => {
      return downloadEvaluationDocument(evaluationId);
    },
  });
};

export const useSingleEvaluation = (evaluationId?: number) => {
  return useQuery<SingleEvaluationResponse, Error>({
    queryKey: ["singleEvaluation", evaluationId],
    queryFn: () => getEvaluation(evaluationId!),
    enabled: !!evaluationId,
    retry: false,
    retryOnMount: false,
  });
};
